import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDraggable } from '@dnd-kit/core';
import { ELEMENT_TYPE_LABELS } from './editorUtils';
import { ELEMENT_TYPE_ICONS } from './elementIcons';

const SavedElementRow = ({ entry, onInsert, onRequestDelete, disabled }) => {
  const type = entry.element?.type;
  const Icon = ELEMENT_TYPE_ICONS[type] || ELEMENT_TYPE_ICONS.text;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `myElement-${entry._id}`,
    data: { from: 'myElements', element: entry.element, label: entry.name },
    disabled,
  });

  return (
    <ListItem
      ref={setNodeRef}
      disablePadding
      sx={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
      secondaryAction={
        <Tooltip title='Element löschen'>
          <IconButton edge='end' size='small' onClick={() => onRequestDelete(entry)}>
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      }
    >
      <ListItemButton onClick={() => !disabled && onInsert(entry.element)} {...attributes} {...listeners}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Icon fontSize='small' />
        </ListItemIcon>
        <ListItemText
          primary={entry.name}
          secondary={ELEMENT_TYPE_LABELS[type] || type}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItemButton>
    </ListItem>
  );
};

// "Meine Elemente" tab: saved reusable elements. Click or drag inserts a deep
// copy with a fresh elementId; entries can be deleted after confirmation.
const MyElementsPanel = ({ elements, loading, onInsert, onDelete, disabled }) => {
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const confirmDelete = async () => {
    const candidate = deleteCandidate;
    setDeleteCandidate(null);
    if (candidate) await onDelete(candidate._id);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Gespeicherte Elemente können per Klick oder Drag &amp; Drop in die E-Mail eingefügt werden.
      </Typography>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && (!elements || elements.length === 0) && (
        <Typography variant='body2' color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>
          Noch keine gespeicherten Elemente vorhanden. Speichern Sie ein Element über das Lesezeichen-Symbol im
          Editor.
        </Typography>
      )}
      {!loading && elements && elements.length > 0 && (
        <List dense disablePadding>
          {elements.map(entry => (
            <SavedElementRow
              key={entry._id}
              entry={entry}
              onInsert={onInsert}
              onRequestDelete={setDeleteCandidate}
              disabled={disabled}
            />
          ))}
        </List>
      )}
      <Dialog open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Element löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Soll das gespeicherte Element "${deleteCandidate?.name || ''}" wirklich gelöscht werden?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCandidate(null)}>Abbrechen</Button>
          <Button color='error' variant='contained' onClick={confirmDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyElementsPanel;
